<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <Teleport to="body">
    <Transition name="search-modal">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[9999]"
        @click="closeSearch"
      >
        <div
          class="absolute inset-0 bg-on-background/70 backdrop-blur-md"
        ></div>

        <button
          type="button"
          class="inline-flex btn-header-search absolute right-6 top-6 z-20 bg-white"
          aria-label="Close search"
          @click="closeSearch"
        >
          <span class="material-symbols-outlined text-[22px]">close</span>
        </button>

        <div
          class="pointer-events-none relative z-10 flex h-full w-full items-start justify-center px-4 pt-28"
        >
          <div
            class="search-container pointer-events-auto w-full max-w-3xl"
            @click.stop
          >
            <div class="relative">
              <input
                ref="searchInputRef"
                v-model="searchQuery"
                type="text"
                placeholder="Search articles..."
                class="search-field"
                @keydown.esc="closeSearch"
                @keydown.enter="handleEnter"
                @keydown.down.prevent="navigateResults(1)"
                @keydown.up.prevent="navigateResults(-1)"
              />
              <span
                class="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[22px] text-primary md:left-4"
              >search</span>
              <button
                v-if="searchQuery"
                type="button"
                class="inline-flex btn-header-search absolute right-2 top-1/2 -translate-y-1/2 md:right-3"
                aria-label="Clear search"
                @click="clearSearch"
              >
                <span class="material-symbols-outlined text-[22px]">close</span>
              </button>
            </div>

            <Transition name="results-fade">
              <div
                v-if="searchQuery && filteredResults.length > 0"
                class="search-results-container search-panel mt-4 max-h-[70vh] overflow-hidden overflow-y-auto md:mt-6"
              >
                <div class="p-4">
                  <TransitionGroup name="result-item" tag="div">
                    <div
                      v-for="(article, index) in filteredResults"
                      :key="article.id"
                      class="result-item-card group hover-target flex cursor-pointer items-start gap-4 border p-4 transition-all duration-300"
                      :class="[
                        selectedIndex === index
                          ? 'border-primary bg-primary/5'
                          : 'border-outline-variant/20 hover:border-primary/40 hover:bg-background',
                      ]"
                      :style="{ 'animation-delay': `${index * 50}ms` }"
                      @click="selectArticle(article)"
                    >
                      <div
                        class="h-20 w-20 shrink-0 overflow-hidden border border-outline-variant/30 bg-surface-container-low"
                      >
                        <img
                          :src="article.image"
                          :alt="article.title"
                          class="h-full w-full object-cover"
                        />
                      </div>
                      <div class="min-w-0 flex-1">
                        <h3
                          class="font-headline mb-2 line-clamp-2 text-left text-lg font-bold tracking-tight text-on-background transition-colors group-hover:text-primary"
                        >
                          {{ article.title }}
                        </h3>
                        <p
                          class="font-body mb-3 line-clamp-3 text-base leading-relaxed text-on-surface-variant"
                        >
                          {{ article.excerpt }}
                        </p>
                        <div
                          class="flex flex-wrap items-center gap-4 text-sm font-medium text-on-surface-variant"
                        >
                          <span
                            class="inline-block border border-outline-variant/30 bg-background px-3 py-1 font-label text-[10px] font-semibold uppercase tracking-[0.15em] text-primary"
                            >{{ article.category }}</span>
                          <div class="mt-2 flex w-full flex-col gap-1 md:hidden">
                            <span>{{ article.date }}</span>
                            <span>{{ article.readTime }}</span>
                          </div>
                          <div class="hidden items-center gap-2 md:flex">
                            <span>{{ article.date }}</span>
                            <span>{{ article.readTime }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TransitionGroup>
                </div>
              </div>
            </Transition>

            <Transition name="results-fade">
              <div
                v-if="searchQuery && filteredResults.length === 0"
                class="no-results-container search-panel mt-4 p-10 text-center md:mt-6 md:p-12"
              >
                <span
                  class="material-symbols-outlined mb-4 block text-5xl text-on-surface-variant/60"
                >
                  search_off
                </span>
                <p class="font-body text-lg font-medium text-on-surface-variant">
                  No articles found for "{{ searchQuery }}"
                </p>
              </div>
            </Transition>

            <div
              v-if="!searchQuery"
              class="search-panel mt-4 p-10 text-center md:mt-6 md:p-12"
            >
              <span
                class="material-symbols-outlined mb-4 block text-4xl text-on-surface-variant/60"
              >
                search
              </span>
              <p class="font-body text-lg font-medium text-on-surface-variant">
                Start typing to search articles...
              </p>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
/* eslint-disable vue/multi-word-component-names */
import { ref, computed, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';

import { normalizeCategories } from "../utils/articleCategories.js";

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  articles: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const router = useRouter();
const searchQuery = ref('');
const searchInputRef = ref(null);
const selectedIndex = ref(-1);

const articlesArray = computed(() => Object.values(props.articles));

const filteredResults = computed(() => {
  if (!searchQuery.value.trim()) return [];

  const query = searchQuery.value.toLowerCase().trim();
  const results = [];

  articlesArray.value.forEach((article) => {
    const titleMatch = article.title.toLowerCase().includes(query);
    const excerptMatch = article.excerpt.toLowerCase().includes(query);
    const categoryMatch = normalizeCategories(
      article.categories || article.category,
    ).some((cat) => cat.toLowerCase().includes(query));
    const authorMatch = article.author?.toLowerCase().includes(query);

    const contentText = article.content
      ? article.content.replace(/<[^>]*>/g, ' ').toLowerCase()
      : '';
    const contentMatch = contentText.includes(query);

    if (titleMatch || excerptMatch || categoryMatch || authorMatch || contentMatch) {
      results.push(article);
    }
  });

  return results.sort((a, b) => {
    const aTitle = a.title.toLowerCase().includes(query);
    const bTitle = b.title.toLowerCase().includes(query);
    if (aTitle && !bTitle) return -1;
    if (!aTitle && bTitle) return 1;
    return 0;
  });
});

const clearSearch = () => {
  searchQuery.value = '';
  selectedIndex.value = -1;
  nextTick(() => {
    searchInputRef.value?.focus();
  });
};

const closeSearch = () => {
  emit('close');
  searchQuery.value = '';
  selectedIndex.value = -1;
};

const selectArticle = (article) => {
  router.push(`/article/${article.id}`);
  closeSearch();
};

const handleEnter = () => {
  if (selectedIndex.value >= 0 && filteredResults.value[selectedIndex.value]) {
    selectArticle(filteredResults.value[selectedIndex.value]);
  }
};

const navigateResults = (direction) => {
  if (filteredResults.value.length === 0) return;

  selectedIndex.value += direction;

  if (selectedIndex.value < 0) {
    selectedIndex.value = filteredResults.value.length - 1;
  } else if (selectedIndex.value >= filteredResults.value.length) {
    selectedIndex.value = 0;
  }
};

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      nextTick(() => {
        searchInputRef.value?.focus();
        searchQuery.value = '';
        selectedIndex.value = -1;
      });
    } else {
      document.body.style.overflow = '';
    }
  },
);
</script>

<style scoped>
.search-modal-enter-active {
  transition: opacity 0.3s ease;
}

.search-modal-leave-active {
  transition: opacity 0.2s ease;
}

.search-modal-enter-from,
.search-modal-leave-to {
  opacity: 0;
}

.search-container {
  animation: slideDown 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.search-results-container::-webkit-scrollbar {
  width: 6px;
}

.search-results-container::-webkit-scrollbar-track {
  background: transparent;
}

.search-results-container::-webkit-scrollbar-thumb {
  background: #c1cab3;
  border-radius: 3px;
}

.results-fade-enter-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.results-fade-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.results-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.results-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.result-item-enter-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.result-item-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 1, 1);
}

.result-item-enter-from {
  opacity: 0;
  transform: translateY(12px) scale(0.98);
}

.result-item-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.98);
}

.result-item-move {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.result-item-card:hover {
  transform: translateY(-1px);
}

.result-item-card:active {
  transform: translateY(0);
}
</style>
